import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import type {CertificatRamonage} from "@mytour/shared";
import {DocumentSummaryLine} from "@mytour/ui/DocumentSummaryLine";
import dayjs from "dayjs";
import {useEffect, useState} from "react";
import {Spinner} from "reactstrap";
import {CertificateRecapModal} from "../recap/CertificateRecapModal";
import type {CertificateEventProps} from "../types";
import {CertificateWizardModal} from "../wizard/CertificateWizardModal";
import {CertificateReadError} from "./CertificateReadError";
import "./CertificateEventLine.css";
import {getEmailStatusBadgeState} from "@mytour/shared";
import {useCertificateAction} from "./useCertificateAction";

type Props = CertificateEventProps;

/**
 * Écrans manager Rdv/Récap/Facturé ; le gate d'affichage est propre à chaque écran
 * (le RDV exige en plus un `event.id` persisté) (#615).
 */
export function CertificateEventLine({
  event,
  customer,
  disabled,
  onPerformed,
  customerQueries,
}: Props) {
  const {
    isLoading,
    hasCertificate,
    needsCompletion,
    certificate,
    latestCertificate,
    company,
    platformUserStyle,
    intervenantName,
    isPending,
    showModal,
    openModal,
    closeModal,
    handleSave,
    handleResume,
    saveError,
    certificateError,
    refetchCertificate,
    isCertificateFetching,
  } = useCertificateAction(event, customer, onPerformed);
  const [recapEventId, setRecapEventId] = useState<string | null>(null);
  const isRecapOpen = !!event.id && recapEventId === event.id && !!certificate?.hasFile;

  useEffect(() => {
    setRecapEventId((openedEventId) => (openedEventId === event.id ? openedEventId : null));
  }, [event.id]);

  if (certificateError && !isRecapOpen) {
    return (
      <CertificateReadError
        eventId={event.id}
        disabled={disabled || isPending}
        isFetching={isCertificateFetching}
        onRetry={refetchCertificate}
      />
    );
  }

  if (isLoading) {
    return null;
  }

  return (
    <div className="certificat-ramonage-line-wrapper">
      {needsCompletion && certificate ? (
        <DocumentSummaryLine
          icon={<FontAwesomeIcon icon="file-pdf" />}
          title={`Attestation de ramonage n° ${certificate.certificateNumber}`}
          subtitle="Document enregistré, PDF à compléter · Reprendre le PDF"
          trailing={isPending ? <Spinner size="sm" /> : undefined}
          onClick={handleResume}
          disabled={disabled || isPending}
        />
      ) : hasCertificate && certificate ? (
        <CreatedLine
          certificate={certificate}
          disabled={disabled || isPending}
          onClick={handleOpenRecap}
        />
      ) : (
        <EmptyLine disabled={disabled} isPending={isPending} onRedigerClick={openModal} />
      )}
      {showModal && company && platformUserStyle && (
        <CertificateWizardModal
          customerQueries={customerQueries}
          company={company}
          platformUserStyle={platformUserStyle}
          defaultValues={latestCertificate ?? undefined}
          customer={customer}
          intervenantName={intervenantName}
          error={saveError}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
      {isRecapOpen && event.id && (
        <CertificateRecapModal
          customerQueries={customerQueries}
          key={event.id}
          eventId={event.id}
          customerId={customer.id}
          onClose={handleCloseRecap}
        />
      )}
    </div>
  );

  function handleOpenRecap() {
    setRecapEventId(event.id ?? null);
  }

  function handleCloseRecap() {
    setRecapEventId(null);
  }
}

function EmptyLine({
  disabled,
  isPending,
  onRedigerClick,
}: {
  disabled: boolean;
  isPending: boolean;
  onRedigerClick: () => void;
}) {
  return (
    <DocumentSummaryLine
      icon={
        <>
          <span className="certificat-ramonage-line-leaf" />
          <span className="certificat-ramonage-line-icon-badge">
            <FontAwesomeIcon icon="pen" />
          </span>
        </>
      }
      iconClassName="certificat-ramonage-line-icon-large"
      title="Attestation de ramonage"
      subtitle={
        <>
          <span className="d-none d-sm-inline">Signature puis envoi par email au client</span>
          <span className="d-sm-none">Signature puis envoi au client</span>
        </>
      }
      trailing={isPending ? <Spinner size="sm" /> : undefined}
      onClick={onRedigerClick}
      disabled={disabled || isPending}
    />
  );
}

function CreatedLine({
  certificate,
  disabled,
  onClick,
}: {
  certificate: CertificatRamonage;
  disabled: boolean;
  onClick: () => void;
}) {
  const createdDate = dayjs(certificate.date);
  const badgeState = getEmailStatusBadgeState(certificate.emailStatus);

  return (
    <DocumentSummaryLine
      icon={
        <>
          <span className="certificat-ramonage-line-leaf" />
          <span
            title={
              badgeState === "failed"
                ? "Échec d'envoi"
                : badgeState === "uncertain"
                  ? "Envoi à vérifier"
                  : badgeState === "not-sent"
                    ? "Non envoyée"
                    : undefined
            }
            className={`certificat-ramonage-line-icon-badge${badgeState ? ` ${badgeState}` : ""}`}
          >
            {badgeState === "failed" || badgeState === "uncertain" ? (
              "!"
            ) : badgeState === "not-sent" ? (
              <FontAwesomeIcon icon="envelope" />
            ) : (
              <FontAwesomeIcon icon="check" />
            )}
          </span>
        </>
      }
      iconClassName="certificat-ramonage-line-icon-large"
      title={`Attestation de ramonage n° ${certificate.certificateNumber}`}
      subtitle={
        <>
          <span className="d-none d-sm-inline">Créée le {createdDate.format("DD/MM/YYYY")}</span>
          <span className="d-sm-none">Créée le {createdDate.format("DD/MM")}</span>
          {badgeState === "failed" && <span className="status-line failed">Email non envoyé</span>}
          {badgeState === "uncertain" && (
            <span className="status-line uncertain">Envoi à vérifier</span>
          )}
          {badgeState === "not-sent" && <span className="status-line not-sent">Non envoyée</span>}
        </>
      }
      onClick={onClick}
      disabled={disabled}
    />
  );
}
